import { LegacyKeyStrings, WorkshopTypeIds, ActiveWorkshopStatuses, AllWorkshopTypes, WorkshopStatusIdStatuses, Config } from "../constants"
import { Language, LanguageIds, Text } from "../language"
import { getDatePortion, NullableDateRange } from "../utility"
import { Participant, ParticipantWorkbooks, RegistrationOperation, RelatedParticipant } from "./participant"
import { Dictionary, DictionaryItem, LegacyJsonResponseSuccess, DropdownData, IdLookup } from "./system"
import { Program, Workshop, WorkshopEvent } from "./workshops"
import { WorkshopMenuItem } from "./workshopMenu"
import { DropdownItem } from "../Components/Dropdown"

export function getDropdownItems(dictionaryItems: Array<DictionaryItem>): Array<DropdownItem<number>> {
	let dropdownItems: Array<DropdownItem<number>> = dictionaryItems.map(di => {
		let value = Number.parseInt(di.value)

		return { value, text: di.text }
	})

	return dropdownItems
}

export function prepDropdownData(response: LegacyJsonResponseSuccess<Array<Dictionary>>, guiLanguage: Language): DropdownData {
	const tier1Dictionary = response.Data.find(d => d.Name == LegacyKeyStrings.LocationTier1)!
	const locationTier1 = getDropdownItems(tier1Dictionary.Value)

	const tier2Dictionary = response.Data.find(d => d.Name == LegacyKeyStrings.LocationTier2)!
	const locationTier2 = getDropdownItems(tier2Dictionary.Value)

	const locationTier2ParentIds = tier2Dictionary.Value.reduce((parentDictionary, dictionaryItem) => {
		parentDictionary[dictionaryItem.value as any as number] = Number.parseInt(dictionaryItem.parentId || '')

		return parentDictionary
	}, {} as IdLookup)

	const provinceDictionary = response.Data.find(d => d.Name == LegacyKeyStrings.Province)!
	const province = getDropdownItems(provinceDictionary.Value)

	const watershedDictionary = response.Data.find(d => d.Name == LegacyKeyStrings.Watershed)!
	const watershed = getDropdownItems(watershedDictionary.Value)

	const commodityDictionary = response.Data.find(d => d.Name == LegacyKeyStrings.CommodityCategory)!
	const commodityCategory = getDropdownItems(commodityDictionary.Value)

	const subCommodityDictionary = response.Data.find(d => d.Name == LegacyKeyStrings.CommoditySubCategory)!
	const commoditySubCategory = getDropdownItems(subCommodityDictionary.Value)

	const commoditySubCategoryParentIds = subCommodityDictionary.Value.reduce((parentDictionary, dictionaryItem) => {
		// TODO: double check what is going on here and how typescript treats numeric keys to objects
		parentDictionary[dictionaryItem.value as any as number] = Number.parseInt(dictionaryItem.parentId || '')

		return parentDictionary
	}, {} as IdLookup)

	const language: Array<DropdownItem<number>> = Object.entries(LanguageIds)
		.map(([language, languageId]) => { return { value: languageId, text: Text[guiLanguage][language as Language] } })

	const workshopType: Array<DropdownItem<number>> =
		AllWorkshopTypes.map(wt => {
			return {
				value: WorkshopTypeIds[wt],
				text: Text[guiLanguage][wt]
			}
		})

	return { locationTier1, locationTier2, locationTier2ParentIds, province, watershed, commodityCategory, commoditySubCategory, commoditySubCategoryParentIds, language, workshopType }
}

export function isEventInTimeRange(event: WorkshopEvent, dateRange: NullableDateRange, ignoreTimes?: boolean): boolean {
	const minDatetime = dateRange[0]
	const firstDatetime = event.Schedule[0].StartDateAndTime
	const lastDatetime = event.Schedule[event.Schedule.length - 1].EndDateAndTime
	const maxDatetime = dateRange[1]

	const [minDate, eventFirstDate, eventLastDate, maxDate] = [minDatetime, firstDatetime, lastDatetime, maxDatetime].map(d => ignoreTimes ? getDatePortion(d) : d)

	const eventIsInTimeRange = (
		(!minDate || !eventFirstDate || minDate <= eventFirstDate) &&
		(!maxDate || !eventLastDate || eventLastDate <= maxDate)
	)

	return eventIsInTimeRange
}

export function prepWorkshopData(response: LegacyJsonResponseSuccess<Array<Program>>) {
	const now = new Date()

	const workshops = response.Data
		// only interested in active programs
		.filter(p => p.IsAvailable)

		// adjust data
		.map(p => {
			p.Workshops.forEach(w => {

				w.Events.forEach(e => {
					// parse date strings
					e.Schedule.forEach(s => {
						s.StartDateAndTime = new Date(s.StartDateAndTime)
						s.EndDateAndTime = new Date(s.EndDateAndTime)

						// let's make sure there are always dummy envents when testing
						if (Config.MockApi) 
						{
							s.StartDateAndTime.setFullYear(now.getFullYear())
							s.EndDateAndTime.setFullYear(now.getFullYear())

							s.StartDateAndTime.setMonth(11)
							s.EndDateAndTime.setMonth(11)
						}
					})
				})

				// filter out inactive events, and ones that are full
				w.Events = w.Events.filter(e => e.IsActive && ActiveWorkshopStatuses[WorkshopStatusIdStatuses[e.StatusId]] && e.Schedule.some(s => s.StartDateAndTime >= now) && e.SeatsLeft > 0)
			})
			return p
		})

		// only interested in workshops with upcoming events
		// .flatMap(p => p.Workshops.filter(w => w.Events.length > 0))
		.flatMap(p => p.Workshops)

	return workshops
}

export function prepParticipantData(response: LegacyJsonResponseSuccess<ParticipantWorkbooks>) {
	// parse date string
	response.Data.participantData.DateCreated = new Date(response.Data.participantData.DateCreated)

	return response.Data.participantData
}

export function prepMenuItems(response: LegacyJsonResponseSuccess<Array<WorkshopMenuItem>>) {
	// parse workshopIds
	response.Data.forEach(mi => mi.workshopId = mi.workshopId && Number.parseInt(mi.workshopId as any as string))

	return response.Data
}

// temporary function for auto generating workshop menu items until such time as we can config the menu in the database
export function paddMenuItems(menuItems: Array<WorkshopMenuItem>, workshops: Array<Workshop>): Array<WorkshopMenuItem> {
	const paddedMenuItems = menuItems.slice()

	workshops.forEach(ws => {
		if (!paddedMenuItems.find(mi => mi.workshopId == ws.WorkshopId)) {
			paddedMenuItems.push({ type: 'item', text: ws.WorkshopName, workshopId: ws.WorkshopId })
		}
	})

	return paddedMenuItems
}

export function prepRegisterPayload(workshopEventId: number, participant: Participant, additionalParticipant?: RelatedParticipant) {
	return {
		oper: 'save',
		participantId: participant.ParticipantId,
		workshopEventId,
		registration: participant,
		additionalParticipants: additionalParticipant && [additionalParticipant]
	}
}

export function getUpdatedWorkshops(workshops: Array<Workshop>, operation: RegistrationOperation, workshopEventId: number, seatsLeft: number, maximumParticipants: number) {
	let workshopEventIndex: number = -1
	const workshopIndex = workshops.findIndex(ws => {
		workshopEventIndex = ws.Events.findIndex(wse => wse.EventId == workshopEventId)

		if (workshopEventIndex >= 0) return true
	})

	if (workshopIndex >= 0 && workshopEventIndex >= 0) {
		let updatedWorkshops = workshops.slice()
		updatedWorkshops[workshopIndex] = Object.assign({}, updatedWorkshops[workshopIndex])
		updatedWorkshops[workshopIndex].Events = updatedWorkshops[workshopIndex].Events.slice()
		updatedWorkshops[workshopIndex].Events[workshopEventIndex] = Object.assign({}, updatedWorkshops[workshopIndex].Events[workshopEventIndex])

		updatedWorkshops[workshopIndex].Events[workshopEventIndex].SeatsLeft = seatsLeft
		updatedWorkshops[workshopIndex].Events[workshopEventIndex].MaximumParticipants = maximumParticipants
		updatedWorkshops[workshopIndex].Events[workshopEventIndex].IsParticipating = (operation == 'Register')

		return updatedWorkshops
	}

	return workshops
}